import {Trans} from '@lingui/macro';
import React, {ReactNode, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useNavigationBlocking} from '../../../../srcCommon/app/navigationBlocking';
import {TabButtonElement} from '../../../analytics/components/tabs/tabButtonElement';
import {FolderBreadcrumb} from '../../../components/folderBreadcrumbs/folderBreadcrumbs';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {CompanyFeaturesEnum} from '../../../models/api/companyModel';
import {DataFieldsTableModel} from '../../../models/api/dataFieldsTableModel';
import {ApiRuleBundle, serializeRuleBundle} from '../../../models/api/ruleBundleModel';
import {RuleModelStrict} from '../../../models/api/ruleModel';
import {deleteAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {getCompany, getFindHasCompanyFeature} from '../../../store/data/company/companySelectors';
import {
  getHasTableBasedRules,
  getShouldRulesWrapTriggerConditions,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {SettingsBanner} from '../../banners/settingsBanner';
import {SettingsBannersEnum} from '../../banners/settingsBannersContext';
import {SettingsBannersHost} from '../../banners/settingsBannersHost';
import {StyledSettingsContent, StyledSettingsMain, StyledSettingsWrapper} from '../../common/settingsWrapper';
import {CustomFields} from '../../editors/commons/customFieldsModel';
import {ExpressionCreationState} from '../../editors/expressions/expressionsContext';
import {createRuleEditorContext, RuleEditorContextProvider} from '../../editors/ruleEditor/ruleEditorContext';
import {useInvalidateDataFieldsTable} from '../../fetchers/dataFieldsTables';
import {settingsHorizontalPaddingStyles} from '../../settingsCommonConstants';
import {RuleRecipe} from '../library/recipes';
import {isActiveBundle, isLoadBalancingBundle} from '../ruleBundleHelpers';
import {getSettingsType, hasUnauthorizedAdvancedRule, parseApiError, trackRuleEvent} from '../ruleHelpers';
import {RuleIntegration} from '../ruleIntegrationModel';
import {
  makeDefaultRuleEditorDataFieldsTable,
  RuleEditorDataFieldsTable,
} from './dataFieldsTable/ruleEditorDataFieldsTable';
import {DeleteDialog} from './dialog/deleteDialog';
import {DiscardChangesDialog} from './dialog/discardChangesDialog';
import {LoadBalancingDialog} from './dialog/loadBalancingDialog';
import {UnauthorizedAdvancedRuleDialog} from './dialog/unauthorizedAdvancedRuleDialog';
import {useEditorState} from './hooks/useEditorState';
import {RuleEditorFooter} from './ruleEditorFooter';
import {RuleEditorForm} from './ruleEditorForm';
import {RuleEditorHeader} from './ruleEditorHeader';
import {RuleEditorRuleNameInput} from './ruleEditorRuleNameInput';
import {RuleEditorSidebar} from './ruleEditorSidebar';
import {RuleEditorTabsEnum} from './ruleEditorTabs';
import {RuleEditorSidePanel} from './sidePanel/ruleEditorSidePanel';
import {RuleEditorState} from './state/ruleEditorState';

/*
 * Constants.
 */

const {toBundle} = RuleEditorState;

/*
 * Types.
 */

interface RuleEditorProps {
  panelId: SettingsPanelId;
  rule?: RuleModelStrict;
  namespace: string;
  recipe?: RuleRecipe;
  ruleIntegration: RuleIntegration;
  hasRuleExpressions: boolean;
  navigateToRules: Handler;
  customFields: CustomFields;
  initialDataTable: DataFieldsTableModel | undefined;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  hasHighlightErrorsFlag: boolean;
}

/*
 * Styles.
 */

export const StyledRuleEditorWrapper = styled.div`
  flex: 1;
  margin-bottom: 48px;
  ${settingsHorizontalPaddingStyles};
  display: flex;
  padding-right: 0;
`;

export const StyledRuleEditorFormWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 260px;
  padding: 0 36px 48px 48px;
`;

/*
 * Component.
 */

export const RuleEditor = ({
  panelId,
  rule,
  namespace,
  recipe,
  ruleIntegration,
  hasRuleExpressions,
  hasHighlightErrorsFlag,
  navigateToRules,
  customFields,
  initialDataTable,
  breadcrumbs,
}: RuleEditorProps) => {
  const company = useAppSelector(getCompany);
  const [shouldShowUnauthorizedAdvancedRuleDialog, setShouldShowUnauthorizedAdvancedRuleDialog] =
    useState(false);
  const [shouldShowLoadBalancingDialog, setShouldShowLoadBalancingDialog] = useState(false);
  const [shouldShowDeleteDialog, setShouldShowDeleteDialog] = useState(false);
  const {setErrorBannerMessage, maybeRenderErrorBanner} = useRuleEditorErrorBanner();
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);
  const hasAdvancedRules = findHasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES);
  const hasUpgradedRules = findHasCompanyFeature(CompanyFeaturesEnum.UPGRADED_RULES);
  const shouldWrapTriggerConditions = useAppSelector(getShouldRulesWrapTriggerConditions);
  const hasTableBasedRules =
    useAppSelector(getHasTableBasedRules) && findHasCompanyFeature(CompanyFeaturesEnum.TABLE_BASED_RULES);

  const initialEditorState = useMemo(
    () => RuleEditorState.createInitialState(rule, customFields, initialDataTable, namespace, recipe),
    [rule, initialDataTable, customFields, namespace, recipe],
  );

  const {ruleEditorState, expressionsDependencies, ...actions} = useEditorState(
    rule,
    customFields,
    ruleIntegration,
    initialDataTable,
    namespace,
    recipe,
  );

  const {dataTable} = ruleEditorState;
  const hasTable = Boolean(dataTable);
  const hasExpressions = Boolean(ruleEditorState.expressions.length > 0);

  const [expressionCreationState, setExpressionCreationState] = useState<ExpressionCreationState>();

  const trackingData = useMemo(
    () => ({
      recipe_id: recipe?.id ?? 'custom_rule',
      rule_id: rule?.id,
      settings_type: getSettingsType(namespace),
    }),
    [recipe?.id, rule?.id, namespace],
  );

  const trackTableSave = useCallback(
    (table: DataFieldsTableModel | undefined) => {
      if (!table) {
        return;
      }

      const isCreation = !initialDataTable;

      trackInterfaceEvent(
        isCreation
          ? InterfaceEventsEnum.SETTINGS_RULE_TABLE_CREATED
          : InterfaceEventsEnum.SETTINGS_RULE_TABLE_EDITED,
        {
          ...trackingData,
          row_count: table.rows.length,
          column_count: table.columns.length,
          list_column_name: table.columns.map((col) => col.name),
          list_column_type: table.columns.map((col) => col.type),
        },
      );
    },
    [initialDataTable, trackingData],
  );

  const editorContext = useMemo(
    () =>
      createRuleEditorContext({
        rule,
        namespace,
        recipe,
        ruleIntegration,
        hasExpressions: hasRuleExpressions,
        hasHighlightErrorsFlag,
        expressions: ruleEditorState.expressions,
        customFields,
        setPanelOpen: () => actions.onSetPanelOpen(true),
        expressionCreationState,
        setExpressionCreationState,
      }),
    [
      rule,
      namespace,
      recipe,
      ruleIntegration,
      hasRuleExpressions,
      hasHighlightErrorsFlag,
      ruleEditorState.expressions,
      customFields,
      expressionCreationState,
      actions,
    ],
  );

  const hasDiff = RuleEditorState.hasDiff(initialEditorState, ruleEditorState);

  // Prevent the user from navigating away when there are unsaved changes.
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, hasDiff);
  const unblockAndNavigateToRules = () => {
    unblockNavigation();
    navigateToRules();
  };

  const onCreateRule = () => {
    const bundle = toBundle(ruleEditorState, namespace, shouldWrapTriggerConditions);
    const hasUnauthorizedAdvancedRules = hasUnauthorizedAdvancedRule(
      hasAdvancedRules,
      hasUpgradedRules,
      bundle,
    );

    if (hasUnauthorizedAdvancedRules) {
      return setShouldShowUnauthorizedAdvancedRuleDialog(true);
    }

    return createRuleBundleAndRedirect();
  };

  const onEditRule = () => {
    const initialBundle = toBundle(initialEditorState, namespace, shouldWrapTriggerConditions);
    const updatedBundle = toBundle(ruleEditorState, namespace, shouldWrapTriggerConditions);
    const wasLoadBalancingBundle = isLoadBalancingBundle(initialBundle);
    const willBeLoadBalancingBundle = isLoadBalancingBundle(updatedBundle);
    const hasDisabledRule = isActiveBundle(initialBundle) && !isActiveBundle(updatedBundle);
    const isChangingLoadBalancing = wasLoadBalancingBundle && (!willBeLoadBalancingBundle || hasDisabledRule);
    const hasUnauthorizedAdvancedRules = hasUnauthorizedAdvancedRule(
      hasAdvancedRules,
      hasUpgradedRules,
      updatedBundle,
    );

    if (hasUnauthorizedAdvancedRules) {
      return setShouldShowUnauthorizedAdvancedRuleDialog(true);
    }

    if (isChangingLoadBalancing) {
      return setShouldShowLoadBalancingDialog(true);
    }

    return updateRuleBundleAndRedirect();
  };

  const onDeleteRule = () => setShouldShowDeleteDialog(true);

  const editorErrors = RuleEditorState.getEditorErrors(
    ruleEditorState,
    editorContext.ruleIntegration,
    hasHighlightErrorsFlag,
  );

  const invalidateDataFieldsTable = useInvalidateDataFieldsTable();

  const createRuleBundleAndRedirect = async () => {
    const bundle = toBundle(ruleEditorState, namespace, shouldWrapTriggerConditions);
    const url = `${company.url}/namespaces/${namespace}/rule_bundles`;
    try {
      trackTableSave(dataTable);

      const newBundle = await postApiAsync(ApiRuleBundle, url, serializeRuleBundle(bundle, dataTable));
      newBundle.rules.forEach((r) =>
        trackRuleEvent(InterfaceEventsEnum.SETTINGS_RULE_CREATE, r, namespace, recipe?.id),
      );
      unblockAndNavigateToRules();
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
    }
  };

  const updateRuleBundleAndRedirect = async () => {
    const updatedBundle = toBundle(ruleEditorState, namespace, shouldWrapTriggerConditions);
    setShouldShowLoadBalancingDialog(false);
    const url = `${company.url}/rule_bundles/${updatedBundle.id}`;
    try {
      trackTableSave(dataTable);

      const newBundle = await putApiAsync(ApiRuleBundle, url, serializeRuleBundle(updatedBundle, dataTable));
      newBundle.rules.forEach((r) =>
        trackRuleEvent(InterfaceEventsEnum.SETTINGS_RULE_UPDATE, r, namespace, recipe?.id),
      );

      invalidateDataFieldsTable(newBundle.dataFieldsTableId, {shouldUncache: true});

      unblockAndNavigateToRules();
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
    }
  };

  const deleteRuleBundleAndRedirect = async () => {
    const bundle = toBundle(ruleEditorState, namespace, shouldWrapTriggerConditions);
    setShouldShowDeleteDialog(false);
    try {
      await deleteAsync(`${company.url}/rule_bundles/${bundle.id}`);
      bundle.rules.forEach((r) =>
        trackRuleEvent(InterfaceEventsEnum.SETTINGS_RULE_DELETE, r, namespace, recipe?.id),
      );
      unblockAndNavigateToRules();
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
    }
  };

  const maybeRenderLoadBalancingDialog = () => {
    if (!shouldShowLoadBalancingDialog) {
      return null;
    }

    return (
      <LoadBalancingDialog
        onOk={updateRuleBundleAndRedirect}
        onCancel={() => setShouldShowLoadBalancingDialog(false)}
      />
    );
  };

  const maybeRenderUnauthorizedAdvancedRuleDialog = () => {
    if (!shouldShowUnauthorizedAdvancedRuleDialog) {
      return null;
    }

    return (
      <UnauthorizedAdvancedRuleDialog onCancel={() => setShouldShowUnauthorizedAdvancedRuleDialog(false)} />
    );
  };

  const maybeRenderDeleteDialog = () => {
    if (!shouldShowDeleteDialog) {
      return null;
    }

    return (
      <DeleteDialog onOk={deleteRuleBundleAndRedirect} onCancel={() => setShouldShowDeleteDialog(false)} />
    );
  };

  const [selectedTab, setSelectedTab] = useState<RuleEditorTabsEnum>(RuleEditorTabsEnum.RULE_STRUCTURE);

  const generateTable = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_TABLE_CREATE_CLICK, trackingData);

    actions.onUpdateTable(makeDefaultRuleEditorDataFieldsTable());
    setSelectedTab(RuleEditorTabsEnum.TABLE);
  }, [actions, trackingData]);

  const onDeleteTable = useCallback(() => {
    actions.onUpdateTable(() => undefined);
    setSelectedTab(RuleEditorTabsEnum.RULE_STRUCTURE);
  }, [actions]);

  const onAddExpression = useCallback(() => {
    actions.onSetPanelOpen(true);
    trackRuleEvent(
      InterfaceEventsEnum.SETTINGS_DYNAMIC_VARIABLE_CREATE_CLICK,
      ruleEditorState.initialRule,
      namespace,
      recipe?.id,
    );
  }, [actions, namespace, recipe?.id, ruleEditorState.initialRule]);

  const rowCount = dataTable?.rows.length;

  const tabs = useMemo(() => {
    if (!hasTableBasedRules || !hasTable) {
      return undefined;
    }

    return (
      <>
        <TabButtonElement
          interactionId="settings-rule-editor-rule-tab"
          name={<Trans id="settings-rule-editor-rule-tab-label">Conditions</Trans>}
          isSelected={selectedTab === RuleEditorTabsEnum.RULE_STRUCTURE}
          onTabClick={() => setSelectedTab(RuleEditorTabsEnum.RULE_STRUCTURE)}
        />
        <TabButtonElement
          interactionId="settings-rule-editor-table-tab"
          name={<Trans id="settings-rule-editor-table-tab-label">Rule set data ({rowCount})</Trans>}
          isSelected={selectedTab === RuleEditorTabsEnum.TABLE}
          onTabClick={() => setSelectedTab(RuleEditorTabsEnum.TABLE)}
        />
      </>
    );
  }, [hasTableBasedRules, hasTable, selectedTab, rowCount]);

  return (
    <RuleEditorContextProvider context={editorContext}>
      <SettingsBannersHost />
      <StyledSettingsWrapper>
        <RuleEditorHeader
          tabs={tabs}
          isReadonly={editorContext.isReadOnly}
          isEditing={Boolean(rule)}
          namespace={namespace}
          isActive={ruleEditorState.ruleState.isActive}
          onUpdateIsActive={actions.onToggleIsActive}
          breadcrumbs={breadcrumbs}
          recipe={recipe}
          onDelete={onDeleteRule}
          hasTable={hasTable}
          onDeleteTable={onDeleteTable}
          buttons={
            <>
              {hasTableBasedRules && !hasTable && (
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={generateTable}
                  leftIcon="tableColumnSmall"
                >
                  <Trans id="rule-editor-generate-rule-table">Create rule set</Trans>
                </Button>
              )}
              {hasRuleExpressions && !hasExpressions && (
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={onAddExpression}
                  leftIcon="sparklesOutlineSmall"
                >
                  Add dynamic variable
                </Button>
              )}
            </>
          }
        />
        <StyledSettingsMain>
          <StyledRuleEditorWrapper>
            <StyledSettingsContent>
              {selectedTab === RuleEditorTabsEnum.RULE_STRUCTURE ? (
                <>
                  <RuleEditorRuleNameInput
                    ruleName={ruleEditorState.ruleState.name}
                    onUpdateRuleName={actions.onUpdateRuleName}
                  />
                  <RuleEditorForm ruleEditorState={ruleEditorState} actions={actions} />
                  {maybeRenderLoadBalancingDialog()}
                  {maybeRenderUnauthorizedAdvancedRuleDialog()}
                  {maybeRenderDeleteDialog()}
                  {maybeRenderErrorBanner()}
                </>
              ) : (
                dataTable && (
                  <RuleEditorDataFieldsTable
                    table={dataTable}
                    onUpdate={actions.onUpdateTable}
                    trackingData={trackingData}
                  />
                )
              )}
            </StyledSettingsContent>
          </StyledRuleEditorWrapper>
          <RuleEditorSidebar
            ruleEditorState={ruleEditorState}
            actions={actions}
            namespace={namespace}
            recipe={recipe}
            selectedTab={selectedTab}
          />
          {didBlockNavigation ? (
            <DiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />
          ) : null}
        </StyledSettingsMain>
        <RuleEditorFooter
          editorErrors={editorErrors}
          stateErrors={ruleEditorState.errors}
          isEditing={Boolean(rule)}
          onCreate={onCreateRule}
          onEdit={onEditRule}
          onCancel={navigateToRules}
        />
      </StyledSettingsWrapper>
      <RuleEditorSidePanel
        ruleEditorState={ruleEditorState}
        expressionsDependencies={expressionsDependencies}
        actions={actions}
      />
    </RuleEditorContextProvider>
  );
};

/*
 * Helpers.
 */

export function useRuleEditorErrorBanner() {
  const [errorBannerMessage, setErrorBannerMessage] = useState<ReactNode | null>(null);

  const maybeRenderErrorBanner = useCallback(() => {
    if (!errorBannerMessage) {
      return null;
    }

    return (
      <SettingsBanner type={SettingsBannersEnum.ERROR} onDismissClick={() => setErrorBannerMessage(null)}>
        {errorBannerMessage}
      </SettingsBanner>
    );
  }, [errorBannerMessage]);

  return {setErrorBannerMessage, maybeRenderErrorBanner};
}
